/**
 * Warehouse Transfer Management Page
 * Lists HTE warehouse transfers with filtering, summary cards,
 * and navigation to create/view transfers.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { isElevatedRole } from '../../constants/queryConfig';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import toast from 'react-hot-toast';
import {
  Truck,
  Plus,
  Search,
  ArrowRight,
  ClipboardCheck,
  Trash2,
  ScanLine,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  PanelRightOpen,
  LayoutGrid,
  ArrowLeft,
  User,
  Sun,
  Moon,
  Globe,
  LogOut,
} from 'lucide-react';
import { hteTransferService, HteTransfer } from '../../services/hteTransferService';
import { inventoryService, IcsTransferSummaryRow, IcsTransferCounts } from '../../services/inventoryService';
import { productionOutputScanService } from '../../services/productionOutputScanService';
import { TransferStatusBadge } from '../../components/TransferStatusBadge';
import CreateTransferView from './components/CreateTransferView';
import IcsTransferDetailView from './components/IcsTransferDetailView';
import DailyTransferOutView from './components/DailyTransferOutView';
import TransferVerification from './TransferVerification';
import ShipOutScannerModal from './components/ShipOutScannerModal';
import SearchableDropdown from '../../components/SearchableDropdown';
import { encodeTransferId, decodeTransferId } from '../../utils/transferIdEncoder';
import { wsService } from '../../services/websocket';

const WarehouseTransferManagement: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const { user, logout } = useAuth();
  // Managers should not see Receive Transfer buttons (that action is for supervisors/operators)
  const isManager = user?.role === 'M' || user?.role === 'manager';
  const { theme, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  usePageTitle(t('warehouseTransfer.title'), t('nav.warehouseTransfer'));

  // Strip legacy ?tab= param if present (Report tab removed)
  useEffect(() => {
    if (searchParams.has('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Derive view, transferNumber, and dailyOutDate from URL pathname
  // URL structure:
  //   /transfers                       → list
  //   /transfers/new                   → create
  //   /transfers/receive               → receive (standalone)
  //   /transfers/daily-out/:date       → dailyOut
  //   /transfers/:transferNumber       → detail (fetches data to decide detail vs edit)
  //   /transfers/:transferNumber/edit  → edit
  //   /transfers/:transferNumber/receive → receive (with transfer pre-populated)
  const urlState = useMemo(() => {
    // pathname is the full path, e.g. /transfers/900008/receive
    // We strip the /transfers or /supervisor/transfers prefix to get the sub-path
    const subPath = location.pathname.replace(/^\/(supervisor\/)?transfers\/?/, '');
    const segments = subPath.split('/').filter(Boolean);

    if (segments.length === 0) {
      return { view: 'list' as const, transferNumber: null, dailyOutDate: undefined as string | undefined };
    }
    if (segments[0] === 'new') {
      return { view: 'create' as const, transferNumber: null, dailyOutDate: undefined as string | undefined };
    }
    if (segments[0] === 'receive') {
      return { view: 'receive' as const, transferNumber: null, dailyOutDate: undefined as string | undefined };
    }
    if (segments[0] === 'daily-out' && segments[1]) {
      return { view: 'dailyOut' as const, transferNumber: null, dailyOutDate: segments[1] };
    }
    // /transfers/:encodedId/...
    // Decode the Sqids hash back to the real transfer number
    const tn = decodeTransferId(segments[0]);
    if (segments[1] === 'edit') {
      return { view: 'edit' as const, transferNumber: tn, dailyOutDate: undefined as string | undefined };
    }
    if (segments[1] === 'receive') {
      return { view: 'receive' as const, transferNumber: tn, dailyOutDate: undefined as string | undefined };
    }
    // /transfers/:encodedId → detail (may become edit after fetch)
    return { view: 'detail' as const, transferNumber: tn, dailyOutDate: undefined as string | undefined };
  }, [location.pathname]);

  const { view, transferNumber: selectedTransferNumber, dailyOutDate } = urlState;

  // selectedTransferData is populated reactively when transferNumber changes
  const [selectedTransferData, setSelectedTransferData] = useState<HteTransfer | null>(null);

  // Fetch transfer data when URL contains a transferNumber (detail or receive views)
  useEffect(() => {
    if (selectedTransferNumber && (view === 'detail' || view === 'receive')) {
      setSelectedTransferData(null); // clear stale data
      hteTransferService.getTransfer(selectedTransferNumber).then(transfer => {
        setSelectedTransferData(transfer);
        // If user navigated to /transfers/:tn but it's a draft, redirect to edit
        if (view === 'detail' && transfer.status === 'draft') {
          navigate(`/transfers/${encodeTransferId(selectedTransferNumber)}/edit`, { replace: true });
        }
      }).catch(() => {
        toast.error(t('warehouseTransfer.transferNotFound') || 'Transfer not found');
        navigate(isMobile ? '/supervisor/transfers' : '/transfers', { replace: true });
      });
    } else if (!selectedTransferNumber) {
      setSelectedTransferData(null);
    }
  }, [selectedTransferNumber, view]);

  // Backward compatibility: redirect old ?transferNumber= links to /transfers/:encodedId
  useEffect(() => {
    const tn = searchParams.get('transferNumber');
    if (tn) {
      navigate(`/transfers/${encodeTransferId(tn)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // ICS Search panel state (primary search)
  const [icsView, setIcsView] = useState<'list' | 'detail'>('list');
  const [selectedIcsTransferNumber, setSelectedIcsTransferNumber] = useState<string | null>(null);
  const [icsSearchResults, setIcsSearchResults] = useState<IcsTransferSummaryRow[]>([]);
  const [icsSearchTotal, setIcsSearchTotal] = useState(0);
  const [counts, setCounts] = useState<IcsTransferCounts | null>(null);
  const [icsPage, setIcsPage] = useState(1);
  const [icsSearching, setIcsSearching] = useState(false);
  const [icsHasSearched, setIcsHasSearched] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Search filters (Date From/To sync with date navigator, can also be edited manually)
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    const start = new Date(now); start.setDate(now.getDate() - now.getDay()); // Sunday
    const y = start.getFullYear(), m = String(start.getMonth() + 1).padStart(2, '0'), d = String(start.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    const start = new Date(now); start.setDate(now.getDate() - now.getDay());
    const days = isMobile ? (searchParams.get('type') !== 'transfer-in' ? 0 : 13) : 6;
    const end = new Date(start); end.setDate(start.getDate() + days);
    const y = end.getFullYear(), m = String(end.getMonth() + 1).padStart(2, '0'), d = String(end.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [locationFrom, setLocationFrom] = useState('');
  const [locationTo, setLocationTo] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [transferSearch, setTransferSearch] = useState('');
  const itemCodeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // KPI card status filter (null = show all)
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // URL-driven type filter (e.g. ?type=transfer-back shows only transfer-back rows)
  const typeFilter = searchParams.get('type');

  // WCO-2536: direction tabs. OUT (transfer-backs / outputs) is the default view; IN
  // (raw-material transfers into HTE) sits behind ?type=transfer-in. The legacy
  // ?type=transfer-back deep-link and the no-param default both resolve to OUT.
  const activeDirection: 'in' | 'out' = typeFilter === 'transfer-in' ? 'in' : 'out';
  const setDirection = (dir: 'in' | 'out') => {
    const next = new URLSearchParams(searchParams);
    if (dir === 'in') next.set('type', 'transfer-in');
    else next.delete('type'); // OUT = clean default URL (still resolves via activeDirection)
    setStatusFilter(null);    // reset the status pill when switching tabs
    setIcsPage(1);
    setSearchParams(next, { replace: true });
  };

  // Collapsible section state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Delete confirmation modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{ transferNumber: string } | null>(null);

  // Ship-out scan modal state
  const [shipOutScanTransfer, setShipOutScanTransfer] = useState<string | null>(null);

  // Desktop detection for sidebar panel (≥1024px)
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Receive sidebar panel state (desktop only)
  const [receivePanelOpen, setReceivePanelOpen] = useState(false);
  const [receiveTransferNumber, setReceiveTransferNumber] = useState<string | undefined>(undefined);

  // VB6 (legacy) transfer visibility — hidden by default
  const [showVb6, setShowVb6] = useState(false);

  // Date navigator state (week/month range like CalendarView)
  type DateRangeMode = '1day' | '1week' | '2week' | 'month';
  const [navDate, setNavDate] = useState(new Date());
  const [rangeMode, setRangeMode] = useState<DateRangeMode>(isMobile ? (searchParams.get('type') !== 'transfer-in' ? '1day' : '1week') : '1week');

  // Compute date range boundaries from navDate + rangeMode
  const dateRange = useMemo(() => {
    const toYYYYMMDD = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    };

    if (rangeMode === '1day') {
      const start = new Date(navDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      return { from: toYYYYMMDD(start), to: toYYYYMMDD(end), startDate: start, endDate: end };
    } else if (rangeMode === '1week') {
      const start = new Date(navDate);
      start.setDate(navDate.getDate() - navDate.getDay()); // Sunday
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Saturday
      return { from: toYYYYMMDD(start), to: toYYYYMMDD(end), startDate: start, endDate: end };
    } else if (rangeMode === '2week') {
      const start = new Date(navDate);
      start.setDate(navDate.getDate() - navDate.getDay()); // Sunday of current week
      const end = new Date(start);
      end.setDate(start.getDate() + 13); // Saturday of next week
      return { from: toYYYYMMDD(start), to: toYYYYMMDD(end), startDate: start, endDate: end };
    } else {
      const start = new Date(navDate.getFullYear(), navDate.getMonth(), 1);
      const end = new Date(navDate.getFullYear(), navDate.getMonth() + 1, 0);
      return { from: toYYYYMMDD(start), to: toYYYYMMDD(end), startDate: start, endDate: end };
    }
  }, [navDate, rangeMode]);

  const navigateDate = (direction: number) => {
    setNavDate(prev => {
      const d = new Date(prev);
      if (rangeMode === '1day') {
        d.setDate(d.getDate() + direction);
      } else if (rangeMode === '1week') {
        d.setDate(d.getDate() + direction * 7);
      } else if (rangeMode === '2week') {
        d.setDate(d.getDate() + direction * 14);
      } else {
        d.setMonth(d.getMonth() + direction);
      }
      return d;
    });
  };

  const goToToday = () => setNavDate(new Date());

  const getDateNavHeaderText = () => {
    const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    const { startDate, endDate } = dateRange;
    if (rangeMode === '1day') {
      return navDate.toLocaleDateString('en-US', { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' });
    }
    if (rangeMode === '1week' || rangeMode === '2week') {
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
    return navDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Short date for mobile
  const getDateNavHeaderShort = () => {
    const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    const { startDate, endDate } = dateRange;
    if (rangeMode === '1day') {
      return navDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return `${fmt(startDate)} - ${fmt(endDate)}`;
  };

  // Check if current week contains today (for hiding Today button on mobile)
  const isCurrentWeek = (() => {
    const today = new Date();
    return today >= dateRange.startDate && today <= dateRange.endDate;
  })();

  // YYYYMMDD → YYYY-MM-DD helper
  const toIsoDate = (yyyymmdd: string) =>
    yyyymmdd.length === 8 ? `${yyyymmdd.slice(0,4)}-${yyyymmdd.slice(4,6)}-${yyyymmdd.slice(6,8)}` : yyyymmdd;
  // YYYY-MM-DD → YYYYMMDD helper
  const toCompactDate = (iso: string) => iso.replace(/-/g, '');

  // Sync dateFrom/dateTo when the date navigator changes
  useEffect(() => {
    setDateFrom(toIsoDate(dateRange.from));
    setDateTo(toIsoDate(dateRange.to));
  }, [dateRange.from, dateRange.to]);

  // WCO-2322: load the active OUTPUTs (number + destination) for the visible range, keyed by
  // production_date (YYYY-MM-DD), to stack above each transfer-back card (number + TO destination).
  const [outputsByDate, setOutputsByDate] = useState<Record<string, Array<{ output_number: string; destination_warehouse: string; ship_status: string }>>>({});
  // Normalize any date shape (YYYYMMDD, YYYYMMDDHHMMSS, YYYY-MM-DD, ISO) to a YYYY-MM-DD key.
  const toDayKey = (v: any): string => {
    if (!v) return '';
    const m = String(v).match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  };
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      productionOutputScanService.getOutputsByRange(toIsoDate(dateRange.from), toIsoDate(dateRange.to))
        .then((outs) => {
          if (cancelled) return;
          const map: Record<string, Array<{ output_number: string; destination_warehouse: string; ship_status: string }>> = {};
          for (const o of outs) {
            const k = toDayKey(o.production_date);
            (map[k] = map[k] || []).push({ output_number: o.output_number, destination_warehouse: o.destination_warehouse, ship_status: o.ship_status });
          }
          setOutputsByDate(map);
        })
        // Keep the last good outputs on a failed/empty reload (the 30s poll or the window-focus
        // refetch). Wiping them here made the "transfers out" flash and vanish when a background
        // refresh errored. First-load failures leave the initial {} untouched.
        .catch(() => { /* keep previous outputsByDate */ });
    };
    load();
    // Poll: an output's received state (production_summary.is_approved → 'Y') is set Oracle-side with no
    // event, so re-fetch periodically + on tab focus to refresh the per-output capsules without a manual reload.
    const id = setInterval(load, 30_000);
    window.addEventListener('focus', load);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', load); };
  }, [dateRange.from, dateRange.to]);

  // Fetch ICS location + warehouse codes for dropdowns
  const { data: locationCodes = [] } = useQuery(
    ['ics-location-codes'],
    () => inventoryService.getLocationCodes()
  );
  const { data: warehouseCodes = [] } = useQuery(
    ['ics-warehouse-codes'],
    () => inventoryService.getWarehouseCodes()
  );

  // Combined warehouse + location options for searchable dropdowns (with group headers)
  const locationOptions = useMemo(() => {
    const whOpts = warehouseCodes.map(wh => ({ value: wh.warehouse_code, label: `${wh.warehouse_code} — ${wh.description}` }));
    const locOpts = locationCodes
      .filter(loc => !warehouseCodes.some(wh => wh.warehouse_code === loc.location_code))
      .map(loc => ({ value: loc.location_code, label: `${loc.location_code} — ${loc.description}` }));
    return [
      ...(locOpts.length ? [{ value: '', label: 'Locations', isGroupHeader: true as const }, ...locOpts] : []),
      ...(whOpts.length ? [{ value: '', label: 'Warehouses', isGroupHeader: true as const }, ...whOpts] : []),
    ];
  }, [warehouseCodes, locationCodes]);

  // WCO-2322: resolve a destination location/warehouse code → its human-readable name (for the TO column).
  const destNameByCode = useMemo(() => {
    const m: Record<string, string> = {};
    for (const loc of locationCodes) if (loc.description) m[loc.location_code] = loc.description;
    for (const wh of warehouseCodes) if (!m[wh.warehouse_code] && wh.description) m[wh.warehouse_code] = wh.description;
    return m;
  }, [locationCodes, warehouseCodes]);

  // Badge counts come from the same filtered search response so they always match the list,
  // pagination, and Items Found. Drafts only count for elevated roles (they can see them).
  const totalCount = counts?.all || 0;
  const draftCount = isElevatedRole(user?.role) ? (counts?.draft || 0) : 0;
  const pendingCount = counts?.pending || 0;
  const completeCount = counts?.complete || 0;

  // Re-run the current search to refresh both the list and the badge counts after a mutation.
  const refetch = () => { if (icsHasSearched) handleIcsSearch(icsPage); };

  const handleTransferCreated = (transferNumber: string) => {
    refetch();
    navigate(`/transfers/${encodeTransferId(transferNumber)}`);
  };

  const transferBase = isMobile ? '/supervisor/transfers' : '/transfers';

  const mobileUserMenu = (
    <div className="relative" ref={mobileMenuRef}>
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="p-1 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
      >
        <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
          <User className="h-5 w-5 text-white" />
        </div>
      </button>
      {mobileMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.username}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</div>
          </div>
          <div className="py-1">
            <button
              onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4 mr-3" /> : <Moon className="h-4 w-4 mr-3" />}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button
              onClick={() => { setLanguage(language === 'en' ? 'zh-TW' : language === 'zh-TW' ? 'es' : 'en'); setMobileMenuOpen(false); }}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Globe className="h-4 w-4 mr-3" />
              {language === 'en' ? 'English' : language === 'zh-TW' ? '繁體中文' : 'Español'}
            </button>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-md"
            >
              <LogOut className="h-4 w-4 mr-3" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Wrap content with mobile header when isMobile
  const wrapMobile = (content: React.ReactNode) => {
    if (!isMobile) return content;
    return (
      <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
        <header className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
          <button
            onClick={() => navigate(transferBase)}
            className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{t('nav.warehouseTransfer') || 'Transfers'}</h1>
          </div>
  {mobileUserMenu}
        </header>
        <main className="flex-1 p-2">{content}</main>
      </div>
    );
  };

  const handleOpenDetail = (transferNumber: string, status?: string, source?: string, transferDate?: string) => {
    // Daily-transfer-out rows open the DailyTransferOutView instead of the normal detail
    if (source === 'daily-transfer-out' && transferDate) {
      // Convert YYYYMMDD → YYYY-MM-DD
      const formatted = transferDate.length === 8
        ? `${transferDate.slice(0, 4)}-${transferDate.slice(4, 6)}-${transferDate.slice(6, 8)}`
        : transferDate;
      navigate(`${transferBase}/daily-out/${formatted}`);
      return;
    }

    // Navigate to edit for drafts, detail for everything else
    // (detail view useEffect will fetch fresh data and redirect drafts to edit if needed)
    if (status === 'draft') {
      navigate(`${transferBase}/${encodeTransferId(transferNumber)}/edit`);
    } else {
      navigate(`${transferBase}/${encodeTransferId(transferNumber)}`);
    }
  };

  const handleBackToList = () => {
    setSelectedTransferData(null);
    refetch();
    navigate(transferBase);
  };

  // Delete draft transfer - open confirmation modal
  const handleDeleteClick = (e: React.MouseEvent, transferNumber: string) => {
    e.stopPropagation(); // Prevent row click from opening detail
    setDeleteConfirm({ transferNumber });
  };

  // Confirm delete (uses cancelTransfer which sets status to 'cancelled')
  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await hteTransferService.cancelTransfer(deleteConfirm.transferNumber);
      toast.success(t('warehouseTransfer.deleteSuccess') || 'Transfer deleted');
      setDeleteConfirm(null);
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete transfer');
    }
  };

  // ICS transfer search handler — always uses 'hte' search type with all filters
  const handleIcsSearch = useCallback(async (pageNum: number = 1) => {
    // Only show loading spinner on initial search, not pagination
    if (pageNum === 1) setIcsSearching(true);
    setIcsHasSearched(true);
    try {
      const result = await inventoryService.searchIcsTransfers({
        search_type: 'hte',
        value: dateFrom ? toCompactDate(dateFrom) : undefined,
        value_to: dateTo ? toCompactDate(dateTo) : undefined,
        location_from: locationFrom || undefined,
        location_to: locationTo || undefined,
        item_code: itemCode || undefined,
        transfer_number: transferSearch || undefined,
        show_vb6: showVb6,
        include_drafts: isElevatedRole(user?.role),
        status: statusFilter || undefined,
        source: activeDirection === 'out' ? 'daily-transfer-out' : undefined,
        source_exclude: activeDirection === 'in' ? 'daily-transfer-out' : undefined,
        page: pageNum,
        limit: isMobile ? 10 : 25,
      });
      setIcsSearchResults(result.transfers);
      setIcsSearchTotal(result.total);
      setCounts(result.counts ?? null);
      setIcsPage(pageNum);
    } catch (err) {
      toast.error('Search failed');
      setIcsSearchResults([]);
      setIcsSearchTotal(0);
      setCounts(null);
    } finally {
      setIcsSearching(false);
    }
  }, [dateFrom, dateTo, locationFrom, locationTo, itemCode, transferSearch, showVb6, statusFilter, typeFilter, user?.role]);

  // Auto-search when any filter changes (dateFrom, dateTo, locationFrom, locationTo are instant;
  // itemCode and transferSearch are debounced to avoid firing on every keystroke)
  useEffect(() => {
    if (!initialLoadDone) {
      setInitialLoadDone(true);
      handleIcsSearch(1);
      return;
    }
    // Debounce only when itemCode changes
    if (itemCodeDebounceRef.current) clearTimeout(itemCodeDebounceRef.current);
    itemCodeDebounceRef.current = setTimeout(() => {
      handleIcsSearch(1);
    }, 300);
    return () => { if (itemCodeDebounceRef.current) clearTimeout(itemCodeDebounceRef.current); };
  }, [handleIcsSearch]);

  // Real-time updates via WebSocket — refresh transfer list (and badge counts) on transfer events
  const handleTransferWsEvent = useCallback(() => {
    if (icsHasSearched) handleIcsSearch(icsPage);
  }, [handleIcsSearch, icsHasSearched, icsPage]);

  useEffect(() => {
    wsService.connect();
    wsService.on('hte-transfer-updated', handleTransferWsEvent);
    wsService.on('hte-transfer-created', handleTransferWsEvent);
    return () => {
      wsService.off('hte-transfer-updated', handleTransferWsEvent);
      wsService.off('hte-transfer-created', handleTransferWsEvent);
    };
  }, [handleTransferWsEvent]);

  const handleIcsRowClick = (row: IcsTransferSummaryRow) => {
    if (row.hte_status) {
      // HTE transfer — open via the HTE detail flow (edit for drafts, detail with ship-out scan for others)
      handleOpenDetail(row.transfer_number, row.hte_status, row.hte_source, row.transfer_date);
    } else {
      // Legacy ICS-only transfer — open read-only IcsTransferDetailView
      setSelectedIcsTransferNumber(row.transfer_number);
      setIcsView('detail');
    }
  };

  const handleIcsBackToList = () => {
    setIcsView('list');
    setSelectedIcsTransferNumber(null);
  };

  // Format YYYYMMDD string to readable date
  const formatIcsDate = (dateStr?: string) => {
    if (!dateStr || dateStr.length !== 8) return dateStr || '-';
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  };

  // WCO-2322: `small` renders a compact capsule so several can stack per day-card (one per output/transfer id).
  // Single source: the shared TransferStatusBadge (also used by the Production Summary header).
  const getStatusBadge = (status: string, small = false) => <TransferStatusBadge status={status} small={small} />;

  const getSourceBadge = (src: string) => {
    if (!src || src === 'manual') return null; // Don't show badge for manual (default)
    const styles: Record<string, string> = {
      'auto-generated': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'daily-transfer-out': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    };
    const labels: Record<string, string> = {
      'auto-generated': t('dailyTransferOut.autoGenerated'),
      'daily-transfer-out': t('dailyTransferOut.dailyOut'),
    };
    return (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${styles[src] || ''}`}>
        {labels[src] || src}
      </span>
    );
  };

  // Desktop: redirect /transfers/receive URL to list with sidebar panel open
  // Mobile: let the full-page TransferVerification render via the route
  useEffect(() => {
    if (view === 'receive' && isDesktop) {
      setReceiveTransferNumber(selectedTransferNumber || undefined);
      setReceivePanelOpen(true);
      navigate('/transfers', { replace: true });
    }
  }, [view, selectedTransferNumber, isDesktop]);

  // Switch-transfer resume prompt: shown when user tries to switch to a different transfer
  // that already has a checkpoint saved by another (or same) supervisor
  const [switchPrompt, setSwitchPrompt] = useState<{ targetTransfer: string; checkpoint: any } | null>(null);

  // Helper: open the receive panel (desktop) or navigate to full-page view (mobile)
  // If switching transfers and the target has a checkpoint, show a prompt first
  const openReceivePanel = async (transferNumber?: string) => {
    if (!isDesktop) {
      navigate(transferNumber ? `/transfers/${encodeTransferId(transferNumber)}/receive` : '/transfers/receive');
      return;
    }
    // If already viewing this transfer, no-op
    if (transferNumber === receiveTransferNumber && receivePanelOpen) return;

    // Check if target transfer has a checkpoint
    if (transferNumber && receivePanelOpen && receiveTransferNumber && transferNumber !== receiveTransferNumber) {
      try {
        const checkpoint = await inventoryService.getReceivingCheckpoint(transferNumber);
        if (checkpoint && checkpoint.scannedBoxes && checkpoint.scannedBoxes.length > 0) {
          setSwitchPrompt({ targetTransfer: transferNumber, checkpoint });
          return;
        }
      } catch { /* proceed without checkpoint */ }
    }

    setReceiveTransferNumber(transferNumber);
    setReceivePanelOpen(true);
  };

  const closeReceivePanel = () => {
    setReceivePanelOpen(false);
    setReceiveTransferNumber(undefined);
  };

  // Mobile: full-page TransferVerification for /transfers/receive
  if (view === 'receive' && !isDesktop) {
    return (
      <TransferVerification
        isMobile
        initialTransferNumber={selectedTransferNumber || undefined}
        onBack={() => navigate('/transfers')}
        onSuccess={() => { refetch(); navigate('/transfers'); }}
      />
    );
  }

  // HTE Detail view - loading state while fetching transfer data
  if (view === 'detail' && selectedTransferNumber && !selectedTransferData) {
    return wrapMobile(
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  // HTE Detail view - uses IcsTransferDetailView for read-only display
  if (view === 'detail' && selectedTransferNumber && selectedTransferData) {
    return wrapMobile(
      <IcsTransferDetailView
        transferData={selectedTransferData}
        onBack={handleBackToList}
        onGoToTransfer={(transferNumber) => {
          // Navigate to the detail URL; the detail-load useEffect fetches
          // fresh data and redirects drafts to /edit (or shows 404 / not-found).
          handleOpenDetail(transferNumber);
        }}
        onReceiveTransfer={() => {
          // Open the receive sidebar panel with this transfer pre-populated
          handleBackToList();
          setTimeout(() => openReceivePanel(selectedTransferData.transfer_number), 100);
        }}
      />
    );
  }

  // Create Transfer view
  if (view === 'create') {
    return (
      <CreateTransferView
        onBack={handleBackToList}
        onCreated={handleTransferCreated}
      />
    );
  }

  // Edit Draft view (same form as create, pre-populated with existing data)
  if (view === 'edit' && selectedTransferNumber) {
    return (
      <CreateTransferView
        onBack={handleBackToList}
        onCreated={handleTransferCreated}
        editTransferNumber={selectedTransferNumber}
      />
    );
  }

  // Daily Transfer Out view (opened from Transfers list row click)
  if (view === 'dailyOut') {
    return wrapMobile(
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <DailyTransferOutView
          initialDate={dailyOutDate}
          onBack={handleBackToList}
          onOpenShipOutScan={(tn) => setShipOutScanTransfer(tn)}
        />
        <ShipOutScannerModal
          isOpen={!!shipOutScanTransfer}
          onClose={() => setShipOutScanTransfer(null)}
          transferNumber={shipOutScanTransfer || undefined}
          onConfirmed={() => { setShipOutScanTransfer(null); queryClient.invalidateQueries('daily-transfer-out'); }}
          requestBy={user?.fullName || user?.full_name || user?.username}
          productionDate={dailyOutDate}
        />
      </div>
    );
  }

  // ICS Detail view
  if (icsView === 'detail' && selectedIcsTransferNumber) {
    return (
      <IcsTransferDetailView
        transferNumber={selectedIcsTransferNumber}
        onBack={handleIcsBackToList}
        onGoToTransfer={(num) => setSelectedIcsTransferNumber(num)}
      />
    );
  }

  // List view — with optional receive sidebar panel
  return (
    <div className={isMobile ? 'min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900' : ''}>
    {/* Mobile header — only when rendered as standalone mobile page */}
    {isMobile && (
      <header className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => navigate('/supervisor')}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <LayoutGrid className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">{t('nav.warehouseTransfer') || 'Transfers'}</h1>
        </div>
        {mobileUserMenu}
      </header>
    )}
    <div className="flex gap-4">
      {/* Left: Transfer list (expands when panel is closed) */}
      <div className={`transition-all duration-300 ${receivePanelOpen && isDesktop ? 'w-[70%]' : 'w-full'} min-w-0`}>
      {/* Combined Search Bar: Date nav + filters in one panel */}
      <div className={`md:bg-white md:dark:bg-gray-800 md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700 flex flex-col md:h-[calc(100vh-7.5rem)] ${isMobile ? 'h-[calc(100vh-4rem)]' : ''}`}>
        {/* Mobile: WCO-2536 direction tabs (OUT default first) */}
        <div className="sm:hidden flex items-center gap-1 p-1 border-b border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setDirection('out')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${activeDirection === 'out' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
          >
            {t('warehouseTransfer.transferBacksTab')}
          </button>
          <button
            onClick={() => setDirection('in')}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${activeDirection === 'in' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}
          >
            {t('warehouseTransfer.transfersInTab')}
          </button>
        </div>
        {/* Mobile: compact date nav — date on left, 1d/7d/14d on right */}
        <div className="sm:hidden flex items-center justify-between px-2 py-1.5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <button onClick={() => navigateDate(-1)} className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700">
              <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-300 shrink-0" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {getDateNavHeaderShort()}
              </span>
            </div>
            <button onClick={() => navigateDate(1)} className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-xs">
            {([['1day', '1d'], ['1week', '7d']] as const).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setRangeMode(mode)}
                className={`px-2.5 py-1 rounded-md transition-colors font-medium ${rangeMode === mode ? 'bg-white dark:bg-gray-500 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* Mobile: search bar */}
        <div className="sm:hidden px-2 py-1.5 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={transferSearch}
              onChange={(e) => setTransferSearch(e.target.value)}
              placeholder={t('warehouseTransfer.transferNumberPlaceholder') || 'Search transfer #...'}
              className="w-full pl-8 pr-7 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-indigo-500"
            />
            {transferSearch && (
              <button
                onClick={() => setTransferSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Desktop: Row 1: Date navigator (centered) + Receive/Create buttons (right) */}
        <div className="hidden sm:flex items-center px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          {/* Left: WCO-2536 direction tabs (OUT default first). Matches right section for centering. */}
          <div className="flex-1 flex items-center">
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-xs">
              <button
                onClick={() => setDirection('out')}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${activeDirection === 'out' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('warehouseTransfer.transferBacksTab')}
              </button>
              <button
                onClick={() => setDirection('in')}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${activeDirection === 'in' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('warehouseTransfer.transfersInTab')}
              </button>
            </div>
          </div>
          {/* Center: date nav */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToToday}
              className="px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
              {t('warehouseTransfer.today')}
            </button>
            <div className="flex items-center gap-1">
              <button onClick={() => navigateDate(-1)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400 hidden sm:block" />
              <span className="text-base font-semibold text-gray-900 dark:text-white text-center">
                {getDateNavHeaderText()}
              </span>
              <button onClick={() => navigateDate(1)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-xs">
              <button
                onClick={() => setRangeMode('1week')}
                className={`px-2.5 py-1 rounded-md transition-colors ${rangeMode === '1week' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('warehouseTransfer.week')}
              </button>
              <button
                onClick={() => setRangeMode('month')}
                className={`px-2.5 py-1 rounded-md transition-colors ${rangeMode === 'month' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              >
                {t('warehouseTransfer.month')}
              </button>
            </div>
          </div>
          {/* Action buttons — right aligned */}
          <div className="flex-1 flex items-center justify-end gap-2">
            {/* Receive Transfer button - hidden for managers */}
            {!isManager && (
              <button
                onClick={() => openReceivePanel()}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${receivePanelOpen ? 'bg-green-700 text-white ring-2 ring-inset ring-green-400' : 'bg-green-600 text-white hover:bg-green-700'}`}
              >
                <ClipboardCheck className="w-4 h-4" />
                <span className="hidden sm:inline">{t('warehouseTransfer.receiveTransfer')}</span>
              </button>
            )}
            {isElevatedRole(user?.role) && (
            <button
              onClick={() => navigate('/transfers/new')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('warehouseTransfer.createTransfer')}</span>
            </button>
            )}
          </div>
        </div>
        {/* Row 2: Filters + Status pills — hidden on mobile */}
        <div className="hidden sm:block px-5 py-3">
          <div className="flex flex-wrap items-end gap-3">
            {/* From dropdown */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('warehouseTransfer.from')}</label>
              <SearchableDropdown
                options={locationOptions}
                value={locationFrom}
                onChange={setLocationFrom}
                placeholder={t('warehouseTransfer.allLocations')}
                inputClassName="!py-1.5 !px-2.5 !text-xs"
                className="w-40"
              />
            </div>
            {/* To dropdown */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('warehouseTransfer.to')}</label>
              <SearchableDropdown
                options={locationOptions}
                value={locationTo}
                onChange={setLocationTo}
                placeholder={t('warehouseTransfer.allLocations')}
                inputClassName="!py-1.5 !px-2.5 !text-xs"
                className="w-40"
              />
            </div>
            {/* Item Code text input */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('warehouseTransfer.itemCodeFilter')}</label>
              <input
                type="text"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value)}
                placeholder={t('warehouseTransfer.itemCodePlaceholder')}
                className="w-36 px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
            {/* Transfer # search input */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('warehouseTransfer.transferNumberSearch')}</label>
              <input
                type="text"
                value={transferSearch}
                onChange={(e) => setTransferSearch(e.target.value)}
                placeholder={t('warehouseTransfer.transferNumberPlaceholder')}
                className="w-36 px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
            {/* Separator + Status pills — pushed to the right */}
            <div className="ml-auto h-6 w-px bg-gray-200 dark:bg-gray-600 self-end mb-0.5" />
            <div className="flex items-end gap-1.5">
              <button
                onClick={() => setStatusFilter(null)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === null ? 'bg-gray-700 dark:bg-gray-200 text-white dark:text-gray-900 border-transparent' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'}`}
              >
                {t('warehouseTransfer.allStatuses')}
                <span className={`text-[10px] font-bold ${statusFilter === null ? 'text-gray-300 dark:text-gray-500' : 'text-gray-400 dark:text-gray-500'}`}>{totalCount}</span>
              </button>
              {isElevatedRole(user?.role) && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'draft' ? null : 'draft')}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === 'draft' ? 'bg-gray-600 dark:bg-gray-300 text-white dark:text-gray-900 border-transparent' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'}`}
              >
                {t('warehouseTransfer.drafts')}
                <span className={`text-[10px] font-bold ${statusFilter === 'draft' ? 'text-gray-300 dark:text-gray-500' : 'text-gray-400 dark:text-gray-500'}`}>{draftCount}</span>
              </button>
              )}
              <button
                onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === 'pending' ? 'bg-yellow-500 dark:bg-yellow-600 text-white border-transparent' : 'bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'}`}
              >
                {t('warehouseTransfer.pending')}
                <span className={`text-[10px] font-bold ${statusFilter === 'pending' ? 'text-yellow-200' : 'text-yellow-500 dark:text-yellow-500'}`}>{pendingCount}</span>
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'complete' ? null : 'complete')}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === 'complete' ? 'bg-green-500 dark:bg-green-600 text-white border-transparent' : 'bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20'}`}
              >
                {t('warehouseTransfer.complete')}
                <span className={`text-[10px] font-bold ${statusFilter === 'complete' ? 'text-green-200' : 'text-green-500 dark:text-green-500'}`}>{completeCount}</span>
              </button>
            </div>
            {/* Loading indicator */}
            {icsSearching && (
              <div className="flex items-end pb-1">
                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Results section */}
        {icsHasSearched && (() => {
          // VB6 / status / type / draft-visibility filtering is now applied server-side, so the
          // page rows are already the correct set. Items Found shows the server-side total.
          const filteredIcsResults = icsSearchResults;
          const filteredTotal = icsSearchTotal;

          // WCO-2322: production OUTPUTS are first-class. Compute the output-only rows HERE (not inside
          // the results branch below) so they render independently of the transfer search result set —
          // otherwise a supervisor (non-elevated → can't see auto-generated drafts) gets an empty
          // transfer list and the empty-state short-circuit hides the day's outputs entirely.
          // outputsByDate is fetched by date range only, so only surface output-only days in the
          // unfiltered (or transfer-back) view; under an active search those rows would be spurious.
          const outputTransferDays = new Set(filteredIcsResults.filter(r => r.hte_source === 'daily-transfer-out').map(r => toDayKey(r.request_date)));
          const outputOnlyEligible = !transferSearch && !itemCode && !locationFrom && !locationTo && !statusFilter && activeDirection === 'out';
          const outputOnlyRows = (outputOnlyEligible ? Object.keys(outputsByDate) : [])
            .filter(day => (outputsByDate[day]?.length ?? 0) > 0 && !outputTransferDays.has(day))
            .sort((a, b) => b.localeCompare(a))
            .map(day => ({ transfer_number: null, request_date: day, transfer_date: day, hte_source: 'daily-transfer-out', hte_status: 'draft', is_complete: 'N' } as unknown as IcsTransferSummaryRow));

          return (
          <div className="border-t border-gray-200 dark:border-gray-600 flex-1 overflow-y-auto min-h-0">
            {/* Results header with count */}
            {filteredIcsResults.length > 0 && (
              <div className="px-3 md:px-5 py-1.5 md:py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {filteredTotal} {t('warehouseTransfer.itemsFound')}
                  {statusFilter && (
                    <span className="ml-2 text-xs text-indigo-500 dark:text-indigo-400">
                      ({statusFilter})
                    </span>
                  )}
                </span>
                <label className="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                  <input type="checkbox" checked={showVb6} onChange={(e) => setShowVb6(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5" />
                  {t('managerGuide.transfers.showVb6')}
                </label>
              </div>
            )}

            {icsSearching ? (
              <div className="p-10 text-center text-gray-500 dark:text-gray-400">
                {t('common.loading')}
              </div>
            ) : (filteredIcsResults.length === 0 && outputOnlyRows.length === 0) ? (
              <div className="p-10 text-center">
                <Search className="mx-auto w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('warehouseTransfer.noIcsResults')}</p>
              </div>
            ) : (() => {
              const canSeeDrafts = isElevatedRole(user?.role);
              const draftRows = canSeeDrafts
                ? filteredIcsResults.filter(r => r.hte_status === 'draft' && r.hte_source !== 'daily-transfer-out')
                : [];
              const baseTransferBacks = filteredIcsResults.filter(r => (r.hte_status === 'pending' || r.hte_status === 'draft') && r.hte_source === 'daily-transfer-out');
              const nonDraftRows = filteredIcsResults.filter(r => r.hte_status !== 'draft' && !(r.hte_status === 'pending' && r.hte_source === 'daily-transfer-out'));
              // outputOnlyRows is hoisted above (computed independently of the transfer result set so
              // outputs still render when the transfer list is empty). See the WCO-2322 note there.
              const pendingTransferBacks = [...outputOnlyRows, ...baseTransferBacks];

              const renderTableHead = () => (
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.transferNumber')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.status')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.requestDate')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.transferDate')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.from')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.to')}
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('warehouseTransfer.operator')}
                    </th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
              );

              // Mobile card layout for small screens
              const renderMobileCard = (row: IcsTransferSummaryRow) => {
                const isIncoming = !(row.hte_transfer_type === 'daily_out' || row.warehouse_code_from === 'HTE' || row.location_code_from === 'HTE');
                const isPendingOrScanned = row.hte_status === 'pending' || row.hte_status === 'scanned';
                const from = row.from_description || row.warehouse_code_from || row.location_code_from || '-';
                const to = row.to_description || row.warehouse_code_to || row.location_code_to || '-';
                const statusColor = row.hte_status === 'complete' || row.is_complete === 'Y' ? 'border-l-green-500'
                  : row.hte_status === 'pending' || row.hte_status === 'scanned' ? 'border-l-amber-500'
                  : row.hte_status === 'draft' ? 'border-l-gray-400'
                  : row.hte_status === 'cancelled' ? 'border-l-red-500'
                  : 'border-l-gray-300';
                // WCO-2322: the day's active output number(s) for a transfer-back row (stacked above it).
                const dayOutputs = row.hte_source === 'daily-transfer-out' ? (outputsByDate[toDayKey(row.request_date)] || []) : [];
                // Shared day status (active outputs share the day's submission lifecycle; transfer carries its own).
                const dayStatusStr = row.hte_status || (row.is_complete === 'Y' ? 'complete' : 'pending');

                return (
                  <div
                    key={row.transfer_number || `day-${toDayKey(row.request_date)}`}
                    onClick={() => handleIcsRowClick(row)}
                    className={`mx-2 my-1.5 px-3 py-2.5 border border-gray-200 dark:border-gray-700 border-l-4 ${statusColor} rounded-lg cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-all`}
                  >
                    {/* WCO-2322: each id (outputs then the transfer) on its own line with its OWN small status capsule;
                        the source/Transfer Back label + chevron sit on the right. */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 space-y-0.5">
                        {dayOutputs.map((o) => (
                          <div key={o.output_number} className="flex items-center gap-2">
                            <span className="font-mono text-base font-bold text-blue-600 dark:text-blue-400">{o.output_number}</span>
                            {getStatusBadge(o.ship_status, true)}
                          </div>
                        ))}
                        {row.transfer_number && (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-base font-bold text-blue-600 dark:text-blue-400">{row.transfer_number}</span>
                            {Number(row.transfer_number) < 900000 && <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">VB6</span>}
                            {getStatusBadge(dayStatusStr, true)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {row.hte_source && getSourceBadge(row.hte_source)}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>

                    {/* Row 2: Date + route + action */}
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatIcsDate(row.request_date)}</span>
                        <span>{from} → {to}</span>
                      </div>
                      {/* Inline action button - hidden for managers */}
                      {isPendingOrScanned && isIncoming && !isManager && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openReceivePanel(row.transfer_number); }}
                          className="px-3 py-1 text-[11px] font-semibold text-white bg-green-500 hover:bg-green-600 rounded-md transition-colors"
                        >
                          {t('warehouseTransfer.receiveTransfer') || 'Receive'}
                        </button>
                      )}
                      {row.hte_status === 'draft' && row.hte_source !== 'daily-transfer-out' && (
                        <button
                          onClick={(e) => handleDeleteClick(e, row.transfer_number)}
                          className="px-3 py-1 text-[11px] font-semibold text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                        >
                          {t('common.delete') || 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              };

              // Desktop table row
              const renderRow = (row: IcsTransferSummaryRow, idx: number) => {
                // WCO-2322: the day's active output(s) for a transfer-back row — computed once, reused in the
                // Transfer Number, FROM, and TO columns so they stack in alignment.
                const dayOutputs = row.hte_source === 'daily-transfer-out' ? (outputsByDate[toDayKey(row.request_date)] || []) : [];
                // Shared day status (active outputs share the day's submission lifecycle; transfer carries its own).
                const dayStatusStr = row.hte_status || (row.is_complete === 'Y' ? 'complete' : 'pending');
                // WCO-2322: transfers are optional — an output-only day has no transfer line in any column.
                const hasTransfer = !!row.transfer_number;
                return (
                <tr
                  key={row.transfer_number || `day-${toDayKey(row.request_date)}`}
                  onClick={() => handleIcsRowClick(row)}
                  className={`
                    cursor-pointer transition-colors text-sm border-b border-gray-100 dark:border-gray-700/50
                    ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                    hover:bg-blue-50/70 dark:hover:bg-blue-900/20
                  `}
                >
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {/* WCO-2322: the day's output number(s) stacked above the transfer, with the Transfer Back tag */}
                    {dayOutputs.map((o, i) => (
                      <div key={o.output_number} className="flex items-center gap-2 h-6 mb-0.5">
                        <span className="font-mono font-medium text-blue-600 dark:text-blue-400">{o.output_number}</span>
                        {i === 0 && getSourceBadge('daily-transfer-out')}
                      </div>
                    ))}
                    {hasTransfer && (
                      <div className="flex items-center gap-2 h-6">
                        <span className="font-mono font-medium text-blue-600 dark:text-blue-400">
                          {row.transfer_number}
                        </span>
                        {Number(row.transfer_number) < 900000 && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">VB6</span>}
                        {row.hte_source && (row.hte_source !== 'daily-transfer-out' || (outputsByDate[toDayKey(row.request_date)] || []).length === 0) && getSourceBadge(row.hte_source)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {/* WCO-2322: one small status capsule per id — each output shows its OWN ship_status, the
                        transfer shows its own workflow status. */}
                    {dayOutputs.map((o) => (
                      <div key={o.output_number} className="flex items-center h-6 mb-0.5">{getStatusBadge(o.ship_status, true)}</div>
                    ))}
                    {hasTransfer && <div className="flex items-center h-6">{getStatusBadge(dayStatusStr, true)}</div>}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {formatIcsDate(row.request_date)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {formatIcsDate(row.transfer_date)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {/* WCO-2322: stack one FROM per number to align with TO; outputs + transfer all ship from the same source (HTE) */}
                    {(() => {
                      const fromVal = receivePanelOpen
                        ? (row.warehouse_code_from || row.location_code_from || '-')
                        : (row.from_description || row.warehouse_code_from || row.location_code_from || '-');
                      return (
                        <>
                          {dayOutputs.map((o) => <div key={o.output_number} className="flex items-center h-6 mb-0.5">{fromVal}</div>)}
                          {hasTransfer && <div className="flex items-center h-6">{fromVal}</div>}
                        </>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {/* WCO-2322: one destination per number — output destination(s) stacked above the transfer's,
                        aligned with the stacked numbers in the Transfer Number column */}
                    {dayOutputs.map((o) => (
                      <div key={o.output_number} className="flex items-center h-6 mb-0.5">{destNameByCode[o.destination_warehouse] || o.destination_warehouse || '-'}</div>
                    ))}
                    {hasTransfer && (
                      <div className="flex items-center h-6">
                        {receivePanelOpen
                          ? (row.warehouse_code_to || row.location_code_to || '-')
                          : (row.to_description || row.warehouse_code_to || row.location_code_to || '-')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                    {row.operator_id || '-'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {/* Receive button — for pending incoming transfers (HTE is destination)
                          Hidden for managers - they should not receive transfers */}
                      {!isManager && (row.hte_status === 'pending' || row.hte_status === 'scanned') && !(
                        row.hte_transfer_type === 'daily_out' ||
                        row.warehouse_code_from === 'HTE' ||
                        row.location_code_from === 'HTE'
                      ) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openReceivePanel(row.transfer_number); }}
                          className="p-1.5 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                          title={t('warehouseTransfer.receiveTransfer') || 'Receive'}
                        >
                          <ClipboardCheck className="w-4 h-4" />
                        </button>
                      )}
                      {/* Ship out scan — for outgoing transfers (HTE is source) */}
                      {(row.hte_status === 'pending' || row.hte_status === 'scanned') && (
                        row.hte_transfer_type === 'daily_out' ||
                        row.warehouse_code_from === 'HTE' ||
                        row.location_code_from === 'HTE'
                      ) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShipOutScanTransfer(row.transfer_number); }}
                          className="p-1.5 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title={t('shipOutScan.title') || 'Ship Out Scan'}
                        >
                          <ScanLine className="w-4 h-4" />
                        </button>
                      )}
                      {row.hte_status === 'draft' && hasTransfer && (
                        <button
                          onClick={(e) => handleDeleteClick(e, row.transfer_number)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title={t('common.delete') || 'Delete'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {!row.hte_status && (
                        <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                      )}
                    </div>
                  </td>
                </tr>
                );
              };

              return (
                <div>
                  {/* Draft transfers section */}
                  {draftRows.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleSection('drafts')}
                        className="w-full px-3 md:px-5 py-1.5 md:py-2 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center gap-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {collapsedSections.drafts
                          ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                          : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                        <span className="text-[11px] md:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('warehouseTransfer.drafts')} ({draftRows.length})
                        </span>
                      </button>
                      {!collapsedSections.drafts && (
                        <>
                          <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full">
                              {renderTableHead()}
                              <tbody>{draftRows.map((row, idx) => renderRow(row, idx))}</tbody>
                            </table>
                          </div>
                          <div className="md:hidden">{draftRows.map(row => renderMobileCard(row))}</div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Pending transfer backs section */}
                  {pendingTransferBacks.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleSection('pendingBacks')}
                        className="w-full px-3 md:px-5 py-1.5 md:py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-gray-600 flex items-center gap-1.5 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        {collapsedSections.pendingBacks
                          ? <ChevronRight className="w-3.5 h-3.5 text-purple-400" />
                          : <ChevronDown className="w-3.5 h-3.5 text-purple-400" />}
                        <span className="text-[11px] md:text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                          {t('warehouseTransfer.pendingTransferBacks')} ({pendingTransferBacks.length})
                        </span>
                      </button>
                      {!collapsedSections.pendingBacks && (
                        <>
                          <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full">
                              {renderTableHead()}
                              <tbody>{pendingTransferBacks.map((row, idx) => renderRow(row, idx))}</tbody>
                            </table>
                          </div>
                          <div className="md:hidden">{pendingTransferBacks.map(row => renderMobileCard(row))}</div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Non-draft transfers section */}
                  {nonDraftRows.length > 0 && (
                    <div>
                      {(draftRows.length > 0 || pendingTransferBacks.length > 0) && (
                        <button
                          onClick={() => toggleSection('transfers')}
                          className="w-full px-3 md:px-5 py-1.5 md:py-2 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center gap-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          {collapsedSections.transfers
                            ? <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                            : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                          <span className="text-[11px] md:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('warehouseTransfer.transfersTab')} ({nonDraftRows.length})
                          </span>
                        </button>
                      )}
                      {!collapsedSections.transfers && (
                        <>
                          <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full">
                              {renderTableHead()}
                              <tbody>{nonDraftRows.map((row, idx) => renderRow(row, idx))}</tbody>
                            </table>
                          </div>
                          <div className="md:hidden">{nonDraftRows.map(row => renderMobileCard(row))}</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
          );
        })()}

        {/* Pagination — outside scrollable area so it's always at the bottom */}
        {icsHasSearched && icsSearchTotal > (isMobile ? 10 : 25) && (
          <div className="shrink-0 px-5 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('common.showing')} {((icsPage - 1) * (isMobile ? 10 : 25)) + 1}–{Math.min(icsPage * (isMobile ? 10 : 25), icsSearchTotal)} {t('common.of')} {icsSearchTotal}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleIcsSearch(icsPage - 1)}
                disabled={icsPage === 1}
                className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() => handleIcsSearch(icsPage + 1)}
                disabled={icsPage * (isMobile ? 10 : 25) >= icsSearchTotal}
                className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('warehouseTransfer.confirmDelete')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('warehouseTransfer.transferNumber')}: <span className="font-mono font-medium">{deleteConfirm.transferNumber}</span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ship-Out Scan Modal */}
      <ShipOutScannerModal
        isOpen={!!shipOutScanTransfer}
        onClose={() => setShipOutScanTransfer(null)}
        transferNumber={shipOutScanTransfer || undefined}
        onConfirmed={() => { setShipOutScanTransfer(null); refetch(); queryClient.invalidateQueries('daily-transfer-out'); }}
        requestBy={user?.fullName || user?.full_name || user?.username}
      />
      </div>{/* end left panel */}

      {/* Right: Receive sidebar panel */}
      {receivePanelOpen && isDesktop && (
        <div className="w-[30%] min-w-[340px] flex-shrink-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm flex flex-col h-[calc(100vh-7.5rem)] sticky top-0">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20 rounded-t-lg">
            <div className="flex items-center gap-2.5">
              <ClipboardCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('transferVerification.title')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('transferVerification.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={closeReceivePanel}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Panel content */}
          <div className="flex-1 overflow-hidden">
            <TransferVerification
              key={receiveTransferNumber || 'general'}
              embedded
              initialTransferNumber={receiveTransferNumber}
              onSuccess={() => {
                closeReceivePanel();
                refetch();
                if (icsHasSearched) handleIcsSearch(icsPage);
              }}
            />
          </div>
        </div>
      )}

      {/* Switch-transfer resume prompt */}
      {switchPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
            <button
              onClick={() => setSwitchPrompt(null)}
              className="absolute top-3 right-3 p-1 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {t('transferVerification.resumeTitle')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              {(t('transferVerification.resumeMessage') || 'Found {count} previously scanned boxes by {name}. Resume or start fresh?')
                .replace('{count}', String(switchPrompt.checkpoint.scannedBoxes?.length || 0))
                .replace('{name}', switchPrompt.checkpoint.supervisorName || '—')}
            </p>
            {switchPrompt.checkpoint.savedAt && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                {new Date(switchPrompt.checkpoint.savedAt).toLocaleString()}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  // Clear checkpoint and switch
                  inventoryService.saveReceivingCheckpoint(switchPrompt.targetTransfer, {
                    supervisorId: '', supervisorName: '', step: 'scanning', scannedBoxes: [], decisionMode: 'perItem',
                  }).catch(() => {
                    toast.error(t('warehouseTransfer.clearCheckpointError'));
                  });
                  setReceiveTransferNumber(switchPrompt.targetTransfer);
                  setSwitchPrompt(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('transferVerification.startFresh')}
              </button>
              <button
                onClick={() => {
                  setReceiveTransferNumber(switchPrompt.targetTransfer);
                  setSwitchPrompt(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('transferVerification.resume')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default WarehouseTransferManagement;
